import React, { useState, useEffect } from 'react';
import { PAIEMENT_ACTIF } from '../config/features';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '../i18n/router';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import axios from 'axios';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import { Voyageurs } from '../models/Voyageurs';
import { API_BASE_URL } from '../config/api';
interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
  titleDisplay?: string;
}

const FormCoreeDuSud: React.FC = () => {
  const { t } = useTranslation('formulaires');
  const { t: tp } = useTranslation('formulaires_pays');
  // Récupération des paramètres passés via react-router
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const titleDisplay = (state as any)?.titleDisplay || (state as any)?.title || '';
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // États initiaux issus des paramètres de route
  const [title, setTitle] = useState<string>(state?.title || '');
  const [prixconsulat, setPrixconsulat] = useState<number>(state?.prixconsulat || 0);
  const [prixintervention, setPrixintervention] = useState<number>(state?.prixintervention || 0);
  const [urlFlag, setUrlFlag] = useState<string>(state?.url_flag || '');
  const [validite, setValidite] = useState<string>(state?.validite || '');
  const commentaires = state?.commentaires || '';
  const [informations, setInformations] = useState<string>(state?.informations || '');

  // Champs du formulaire communs
  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFees] = useState<boolean>(false);
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // Champs spécifiques à la Corée du Sud
  const [visiteCoree, setVisiteCoree] = useState<string>('');
  const [butSejour, setButSejour] = useState<string>('');
  const [adresseSejourCoree, setAdresseSejourCoree] = useState<string>('');
  const [telephoneContactCoree, setTelephoneContactCoree] = useState<string>('');

  // Calcul du total
  const total =
    prixconsulat +
    prixintervention +
    (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0);

  // Fonction de formatage de la date (jj/mm/aaaa)
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Validation du formulaire
  const validateForm = () => {
    const parseDate = (dateStr: string): Date | null => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return null;
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);

    const errors: string[] = [];

    // Contrôle des dates
    if (departDate) {
      if (isNaN(departDate.getTime()) || departDate <= today) {
        errors.push(tp('coreeDuSud.errors.departureDateInvalid'));
      }
    } else {
      errors.push(tp('coreeDuSud.errors.departureDateRequired'));
    }

    if (dateSortie && sortieDate) {
      if (isNaN(sortieDate.getTime()) || (departDate && sortieDate <= departDate)) {
        errors.push(tp('coreeDuSud.errors.returnDateInvalid'));
      }
    } else if (dateSortie && !sortieDate) {
      errors.push(tp('coreeDuSud.errors.returnDateRequired'));
    }

    // Vérification des autres champs obligatoires
    if (!selectedVoyageur) errors.push(t('common.validation.travelerNotSelected'));
    if (!dateDepart.trim()) errors.push(t('common.validation.departureDate'));
    if (!dateSortie.trim()) errors.push(t('common.validation.returnDate'));
    if (!visiteCoree.trim()) errors.push(tp('coreeDuSud.validation.visiteCoree'));
    if (!butSejour.trim()) errors.push(tp('coreeDuSud.validation.butSejour'));
    if (!adresseSejourCoree.trim()) errors.push(tp('coreeDuSud.validation.adresseSejourCoree'));
    if (!telephoneContactCoree.trim()) errors.push(tp('coreeDuSud.validation.telephoneContactCoree'));
    if (!assuranceVisa.trim()) errors.push(t('common.validation.insurance'));
    if (!sliderValue) errors.push(t('common.validation.cguAcceptance'));

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateDepart,
    dateSortie,
    visiteCoree,
    butSejour,
    adresseSejourCoree,
    telephoneContactCoree,
    assuranceVisa,
    sliderValue,
    selectedVoyageur,
  ]);

  // Récupération de la liste des voyageurs
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        // Sélection automatique s’il n’y a qu’un seul voyageur
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true); // Par défaut, considérer comme société
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(v => v.id.toString() === selectedVoyageur);

  // Empêche la fermeture de l’onglet sans confirmation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Bouton retour
  const handleBack = () => {
    if (window.confirm(t('common.confirmation.unsavedChanges'))) {
      navigate(-1);
    }
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId: string | null = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }

      const etatFormulaire = champsNonRemplis.length === 0;

      // Construction du FormData
      const formData = new FormData();
      formData.append('etatFormulaire', String(etatFormulaire));
      formData.append('idUtilisateur', userId || '');
      formData.append('idVoyageur', selectedVoyageur);
      formData.append('titreVisa', title);
      formData.append('dateDepart', dateDepart);
      formData.append('dateSortie', dateSortie);
      formData.append('assuranceVisa', assuranceVisa);
      formData.append('prixconsulat', String(prixconsulat));
      formData.append('prixintervention', String(prixintervention));
      formData.append('validite', validite);
      formData.append('visiteCoree', visiteCoree);
      formData.append('butSejour', butSejour);
      formData.append('adresseSejourCoree', adresseSejourCoree);
      formData.append('telephoneContactCoree', telephoneContactCoree);

      // Envoi vers le backend
      const response = await axios.post(`${API_BASE_URL}/validerDemande`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Erreur lors de la validation de la demande:', error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (réinitialiser le formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setVisiteCoree('');
    setButSejour('');
    setAdresseSejourCoree('');
    setTelephoneContactCoree('');
  };

  // Fonction vide pour le onClick si désactivé
  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              {t('success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('success.message', { title: titleDisplay })}
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société) */}
              {isClientSociete === false && !hideFees && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  {PAIEMENT_ACTIF ? t('success.payOnline') : t('success.launchDemande')}
                </Button>
              )}

              {/* Bouton Créer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                {t('success.newRequest')}
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                {t('success.viewRequests')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box component="form" noValidate autoComplete="off" sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              {t('common.formTitle', { title: titleDisplay })}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur si non en mode édition */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">{t('common.selectTraveler')}</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label={t('common.selectTraveler')}
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map(v => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? t('common.selectTravelerWithPassport', { passport: v.numeropax }) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#D89D2F',
                    borderColor: '#D89D2F',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#B5811F'
                    }
                  }}
                >
                  {t('common.createNewTraveler')}
                </Button>
              </Box>
            </>
          )}

          {/* Avatar du voyageur sélectionné */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt={t('common.alt.avatar')}
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          <TextField
            label={t('common.departureDate.label')}
            placeholder={t('common.departureDate.placeholder')}
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('common.returnDate.label')}
            placeholder={t('common.returnDate.placeholder')}
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Champs spécifiques à la Corée du Sud */}
          <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
            <InputLabel id="visite-coree-label">{tp('coreeDuSud.fields.visiteCoree.label')}</InputLabel>
            <Select
              labelId="visite-coree-label"
              id="visite-coree-select"
              value={visiteCoree}
              label={tp('coreeDuSud.fields.visiteCoree.label')}
              onChange={(e: SelectChangeEvent) => setVisiteCoree(e.target.value)}
            >
              <MenuItem value="Oui">{tp('coreeDuSud.ouiNon.oui')}</MenuItem>
              <MenuItem value="Non">{tp('coreeDuSud.ouiNon.non')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="but-sejour-label">{tp('coreeDuSud.fields.butSejour.label')}</InputLabel>
            <Select
              labelId="but-sejour-label"
              id="but-sejour-select"
              value={butSejour}
              label={tp('coreeDuSud.fields.butSejour.label')}
              onChange={(e: SelectChangeEvent) => setButSejour(e.target.value)}
            >
              <MenuItem value="Tourisme">{tp('coreeDuSud.butSejourOptions.tourisme')}</MenuItem>
              <MenuItem value="Affaires">{tp('coreeDuSud.butSejourOptions.affaires')}</MenuItem>
              <MenuItem value="Traitement médical">{tp('coreeDuSud.butSejourOptions.traitementMedical')}</MenuItem>
              <MenuItem value="Formation">{tp('coreeDuSud.butSejourOptions.formation')}</MenuItem>
              <MenuItem value="Évènement">{tp('coreeDuSud.butSejourOptions.evenement')}</MenuItem>
              <MenuItem value="Évènement religieux">{tp('coreeDuSud.butSejourOptions.evenementReligieux')}</MenuItem>
              <MenuItem value="Évènement sportif">{tp('coreeDuSud.butSejourOptions.evenementSportif')}</MenuItem>
              <MenuItem value="Exposition">{tp('coreeDuSud.butSejourOptions.exposition')}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label={tp('coreeDuSud.fields.adresseSejourCoree.label')}
            placeholder={tp('coreeDuSud.fields.adresseSejourCoree.placeholder')}
            value={adresseSejourCoree}
            onChange={(e) => setAdresseSejourCoree(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('coreeDuSud.fields.telephoneContactCoree.label')}
            placeholder={tp('coreeDuSud.fields.telephoneContactCoree.placeholder')}
            value={telephoneContactCoree}
            onChange={(e) => setTelephoneContactCoree(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Module Assurance identique à celui de FormInde */}
          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>

          {/* Récapitulatif du total */}
          {!hideFees && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">{t('common.fees.visa', { prix: prixconsulat })}</Typography>
              <Typography variant="body1">{t('common.fees.service', { prix: prixintervention })}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {t('common.fees.total', { total })}
              </Typography>
            </Box>
          )}

          {/* Affichage de l'état du formulaire */}
          <Box sx={{ my: 2, p: 2, borderRadius: 1, backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc' }}>
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  {t('common.formStatus.incomplete')}
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">{t('common.formStatus.complete')}</Typography>
            )}
          </Box>

          {/* Bouton de validation */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                {t('common.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormCoreeDuSud;
